import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Crown, Building2, Users, KeyRound, UserPlus, TrendingUp, Bot, ScrollText, LogOut,
  Plus, Copy, RefreshCw, Stethoscope, ArrowUpRight, ArrowDownLeft, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const rand = (n) => "R" + Number(n || 0).toLocaleString("en-ZA");

export default function OwnerConsole() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({ name: "", practice_type: "dental", principal_email: "", principal_name: "", monthly_rate: 250 });

  const { data: practices = [] } = useQuery({ queryKey: ["o-practices"], queryFn: async () => (await api.get("/owner/practices")).data });
  const { data: stats } = useQuery({ queryKey: ["o-stats"], queryFn: async () => (await api.get("/owner/stats")).data });
  const { data: onboarding } = useQuery({ queryKey: ["o-onboarding"], queryFn: async () => (await api.get("/owner/onboarding-queue")).data });
  const { data: automations } = useQuery({ queryKey: ["o-automations"], queryFn: async () => (await api.get("/owner/automations")).data });
  const { data: audit = [] } = useQuery({ queryKey: ["o-audit"], queryFn: async () => (await api.get("/owner/audit")).data });

  const createMut = useMutation({
    mutationFn: async (body) => (await api.post("/owner/practices", body)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["o-practices"] }); qc.invalidateQueries({ queryKey: ["o-stats"] });
      const link = `${window.location.origin}/invite/${d.invite_token}`;
      setInvite(link); toast.success("Practice whitelisted — magic invite link ready");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const rotateMut = useMutation({
    mutationFn: async () => (await api.post("/owner/rotate-hmac")).data,
    onSuccess: () => toast.success("n8n HMAC secret rotated"),
    onError: (e) => toast.error(apiErr(e)),
  });

  const practitioners = practices.reduce((a, p) => a + (p.user_count || 0), 0);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border/50 glass px-5 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Crown className="h-5 w-5" /></div>
          <div>
            <div className="text-sm font-bold tracking-tight leading-none">Owner Console</div>
            <div className="text-[11px] text-muted-foreground">{user?.email}</div>
          </div>
          <Badge variant="secondary" className="ml-2 gap-1 rounded-full text-[10px]"><ShieldCheck className="h-3 w-3" /> 2FA verified</Badge>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button variant="ghost" size="icon" data-testid="owner-logout" className="rounded-full" onClick={() => { logout(); navigate("/login"); }}><LogOut className="h-4 w-4" /></Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Cross-practice overview</h1>
        <p className="mt-1 text-sm text-muted-foreground">Every practice, every automation, one console.</p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric icon={Building2} label="Practices" value={stats?.total_practices ?? "—"} sub={`${stats?.active_practices ?? 0} active`} testid="metric-practices" />
          <Metric icon={TrendingUp} label="MRR" value={rand(stats?.mrr)} sub="Monthly recurring" testid="metric-mrr" />
          <Metric icon={Users} label="Patients" value={stats?.total_patients ?? "—"} sub="Across all tenants" testid="metric-patients" />
          <Metric icon={UserPlus} label="Pending" value={stats?.pending_practices ?? 0} sub="Awaiting activation" testid="metric-pending" />
        </div>

        <Tabs defaultValue="practices" className="mt-8">
          <TabsList data-testid="owner-tabs" className="flex-wrap">
            <TabsTrigger value="practices" data-testid="otab-practices">Practices</TabsTrigger>
            <TabsTrigger value="practitioners" data-testid="otab-practitioners">Practitioners</TabsTrigger>
            <TabsTrigger value="licenses" data-testid="otab-licenses">Licenses</TabsTrigger>
            <TabsTrigger value="onboarding" data-testid="otab-onboarding">Onboarding</TabsTrigger>
            <TabsTrigger value="revenue" data-testid="otab-revenue">Revenue</TabsTrigger>
            <TabsTrigger value="automations" data-testid="otab-automations">Automations</TabsTrigger>
            <TabsTrigger value="audit" data-testid="otab-audit">Audit</TabsTrigger>
          </TabsList>

          {/* Practices */}
          <TabsContent value="practices" className="mt-6 space-y-4">
            <div className="flex justify-end">
              <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setInvite(null); }}>
                <DialogTrigger asChild>
                  <Button data-testid="whitelist-practice-btn" className="gap-2 rounded-full"><Plus className="h-4 w-4" /> Whitelist practice</Button>
                </DialogTrigger>
                <DialogContent className="bg-popover">
                  <DialogHeader>
                    <DialogTitle>Whitelist a new practice</DialogTitle>
                    <DialogDescription>Creates the tenant + principal invite. Same-day setup, no consultant.</DialogDescription>
                  </DialogHeader>
                  {invite ? (
                    <div className="space-y-3">
                      <div className="rounded-xl bg-secondary p-3 text-sm">
                        <div className="label-eyebrow mb-1">Magic invite link</div>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 truncate text-xs" data-testid="invite-link">{invite}</code>
                          <Button size="icon" variant="ghost" className="rounded-full" onClick={() => { navigator.clipboard.writeText(invite); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button>
                        </div>
                      </div>
                      <Button className="w-full rounded-full" onClick={() => { setOpen(false); setInvite(null); }}>Done</Button>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2"><Label>Practice name</Label><Input data-testid="practice-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                        <div><Label>Principal name</Label><Input data-testid="principal-name" value={form.principal_name} onChange={(e) => setForm({ ...form, principal_name: e.target.value })} /></div>
                        <div><Label>Principal email</Label><Input data-testid="principal-email" value={form.principal_email} onChange={(e) => setForm({ ...form, principal_email: e.target.value })} /></div>
                        <div><Label>Type</Label><Input data-testid="practice-type" value={form.practice_type} onChange={(e) => setForm({ ...form, practice_type: e.target.value })} /></div>
                        <div><Label>Monthly rate (R)</Label><Input type="number" data-testid="practice-rate" value={form.monthly_rate} onChange={(e) => setForm({ ...form, monthly_rate: Number(e.target.value) })} /></div>
                      </div>
                      <DialogFooter>
                        <Button data-testid="practice-create-submit" className="rounded-full" disabled={!form.name || !form.principal_email || !form.principal_name || createMut.isPending} onClick={() => createMut.mutate(form)}>Create & invite</Button>
                      </DialogFooter>
                    </>
                  )}
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {practices.map((p) => (
                <Card key={p.id} data-testid={`practice-${p.id}`} className="lift rounded-2xl border-border/60 p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Stethoscope className="h-5 w-5" /></div>
                      <div>
                        <div className="font-semibold">{p.name}</div>
                        <div className="text-xs text-muted-foreground capitalize">{p.practice_type} · {p.principal_email}</div>
                      </div>
                    </div>
                    <Badge variant={p.status === "active" ? "secondary" : "outline"} className="rounded-full capitalize">{p.status}</Badge>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center text-sm">
                    <div className="rounded-lg bg-secondary/50 p-2"><div className="font-bold">{p.patient_count}</div><div className="text-[10px] uppercase text-muted-foreground">Patients</div></div>
                    <div className="rounded-lg bg-secondary/50 p-2"><div className="font-bold">{p.user_count}</div><div className="text-[10px] uppercase text-muted-foreground">Users</div></div>
                    <div className="rounded-lg bg-secondary/50 p-2"><div className="font-bold">{rand(p.monthly_rate)}</div><div className="text-[10px] uppercase text-muted-foreground">/ month</div></div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Practitioners */}
          <TabsContent value="practitioners" className="mt-6">
            <Card className="rounded-2xl border-border/60 p-6">
              <div className="flex items-center gap-2 text-sm"><Users className="h-4 w-4 text-primary" /> {practitioners} users provisioned across {practices.length} practices.</div>
              <p className="mt-2 text-xs text-muted-foreground">Cross-practice reads are architecturally impossible — every non-owner document is pinned to a practice_id.</p>
            </Card>
          </TabsContent>

          {/* Licenses */}
          <TabsContent value="licenses" className="mt-6 space-y-3">
            {practices.map((p) => (
              <Card key={p.id} className="flex items-center justify-between rounded-2xl border-border/60 p-4">
                <div><div className="font-medium">{p.name}</div><div className="text-xs text-muted-foreground">{p.licence_practitioners} practitioners · {p.licence_patients} patients</div></div>
                <Badge variant="outline" className="rounded-full">{rand(p.monthly_rate)}/mo</Badge>
              </Card>
            ))}
          </TabsContent>

          {/* Onboarding */}
          <TabsContent value="onboarding" className="mt-6 grid gap-6 lg:grid-cols-2">
            <Card className="rounded-2xl border-border/60 p-5">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><UserPlus className="h-4 w-4 text-primary" /> Pending practice activations</h3>
              {onboarding?.pending_activations?.length ? onboarding.pending_activations.map((u) => (
                <div key={u.id} className="flex items-center justify-between border-b border-border/50 py-2 text-sm last:border-0">
                  <span>{u.email}</span><Badge variant="outline" className="rounded-full">pending</Badge>
                </div>
              )) : <p className="text-sm text-muted-foreground">All practices activated.</p>}
            </Card>
            <Card className="rounded-2xl border-border/60 p-5">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Bot className="h-4 w-4 text-primary" /> Patient intake leads</h3>
              {onboarding?.patient_leads?.length ? onboarding.patient_leads.map((l) => (
                <div key={l.id} className="flex items-center justify-between border-b border-border/50 py-2 text-sm last:border-0">
                  <div><div className="font-medium">{l.full_name}</div><div className="text-xs text-muted-foreground">{l.phone}</div></div>
                  <Badge variant="outline" className="rounded-full">{l.onboarding_status}</Badge>
                </div>
              )) : <p className="text-sm text-muted-foreground">No leads in progress.</p>}
            </Card>
          </TabsContent>

          {/* Revenue */}
          <TabsContent value="revenue" className="mt-6">
            <Card className="rounded-2xl border-border/60 p-6">
              <div className="text-sm text-muted-foreground">Monthly recurring revenue</div>
              <div className="mt-1 text-4xl font-bold tracking-tight">{rand(stats?.mrr)}</div>
              <div className="mt-4 space-y-2">
                {practices.filter((p) => p.status === "active").map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm"><span>{p.name}</span><span className="font-medium">{rand(p.monthly_rate)}</span></div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Automations */}
          <TabsContent value="automations" className="mt-6 space-y-4">
            <div className="flex items-center justify-between rounded-2xl border border-border/60 bg-secondary/40 p-4">
              <div className="flex items-center gap-2 text-sm"><KeyRound className="h-4 w-4 text-primary" /> n8n events are <strong>MOCKED</strong> — payloads are logged, nothing is dispatched.</div>
              <Button size="sm" variant="outline" data-testid="rotate-hmac-btn" onClick={() => rotateMut.mutate()} className="gap-1 rounded-full"><RefreshCw className="h-3.5 w-3.5" /> Rotate HMAC</Button>
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              <Card className="rounded-2xl border-border/60 p-5">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><ArrowUpRight className="h-4 w-4 text-primary" /> Outbound events ({automations?.outbound_events?.length || 0})</h3>
                <div className="max-h-80 space-y-2 overflow-auto">
                  {automations?.outbound_events?.map((e) => (
                    <div key={e.id} data-testid={`event-${e.id}`} className="rounded-lg bg-secondary/50 p-2.5 text-xs">
                      <div className="flex items-center justify-between"><code className="font-semibold text-primary">{e.event_type}</code><Badge variant="outline" className="rounded-full text-[10px]">{e.status}</Badge></div>
                      <div className="mt-1 truncate text-muted-foreground">{e.target_webhook}</div>
                    </div>
                  )) || <p className="text-sm text-muted-foreground">No events yet.</p>}
                </div>
              </Card>
              <Card className="rounded-2xl border-border/60 p-5">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><ArrowDownLeft className="h-4 w-4 text-primary" /> Inbound webhooks ({automations?.inbound_webhooks?.length || 0})</h3>
                <div className="max-h-80 space-y-2 overflow-auto">
                  {automations?.inbound_webhooks?.length ? automations.inbound_webhooks.map((w) => (
                    <div key={w.id} className="rounded-lg bg-secondary/50 p-2.5 text-xs">
                      <div className="flex items-center justify-between"><code className="font-semibold">{w.source}</code><Badge variant={w.verified ? "secondary" : "destructive"} className="rounded-full text-[10px]">{w.verified ? "verified" : "unverified"}</Badge></div>
                      <div className="mt-1 text-muted-foreground">{w.action_taken}</div>
                    </div>
                  )) : <p className="text-sm text-muted-foreground">No inbound webhooks received.</p>}
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Audit */}
          <TabsContent value="audit" className="mt-6">
            <Card className="overflow-hidden rounded-2xl border-border/60">
              <div className="border-b border-border/60 px-5 py-3 text-sm font-semibold flex items-center gap-2"><ScrollText className="h-4 w-4 text-primary" /> POPIA audit trail</div>
              <div className="max-h-[500px] divide-y divide-border/60 overflow-auto">
                {audit.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 px-5 py-2.5 text-xs">
                    <Badge variant="outline" className="rounded-full">{a.action}</Badge>
                    <span className="font-medium">{a.resource}</span>
                    <span className="text-muted-foreground">{a.actor_email}</span>
                    <span className="ml-auto text-muted-foreground">{new Date(a.timestamp).toLocaleString("en-ZA")}</span>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card data-testid={testid} className="lift rounded-2xl border-border/60 p-5">
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">{label}</span>
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" /></div>
      </div>
      <div className="mt-3 text-2xl font-bold tracking-tight">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
    </Card>
  );
}
